import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Receita {
  id: string;
  workspaceId: string;
  acordoId: string | null;
  competencia: string;
  fonte: string;
  descricao: string | null;
  valor: string;
  dataRecebimento: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ReceitasListResult {
  items: Receita[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReceitasAggregate {
  competencia: string | null;
  count: number;
  total: string;
}

export interface ReceitaFilters {
  competencia?: string;
  search?: string;
  dataInicio?: string;
  dataFim?: string;
  limit?: number;
  offset?: number;
}

export interface CreateReceitaInput {
  competencia: string;
  fonte: string;
  descricao?: string;
  valor: string;
  dataRecebimento: string;
  acordoId?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/receitas`;
}

function buildQuery(filters: ReceitaFilters): string {
  const q = new URLSearchParams();
  if (filters.competencia) q.set('competencia', filters.competencia);
  if (filters.search) q.set('search', filters.search);
  if (filters.dataInicio) q.set('dataInicio', filters.dataInicio);
  if (filters.dataFim) q.set('dataFim', filters.dataFim);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useReceitas(workspaceId: string | null | undefined, filters: ReceitaFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['receitas', workspaceId, filters],
    queryFn: () => api.get<ReceitasListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function useReceitasAggregate(
  workspaceId: string | null | undefined,
  competencia: string | undefined,
) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['receitas', workspaceId, 'aggregate', competencia ?? null],
    queryFn: () =>
      api.get<ReceitasAggregate>(
        `${basePath(workspaceId!)}/aggregate${competencia ? `?competencia=${competencia}` : ''}`,
      ),
  });
}

export function useCreateReceita(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReceitaInput) =>
      api.post<Receita>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receitas', workspaceId] });
    },
  });
}

export function useDeleteReceita(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receitas', workspaceId] });
    },
  });
}
